// Stimulus controller that manages Vimeo video playback in modals on the how-it-works page
// Plays videos with sound when modals open and pauses them when closed using the Vimeo Player API

import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="how-it-works-video-modal"

// Wait for Vimeo iframe to be fully initialized before attempting playback
const IFRAME_READY_DELAY = 100

// Vimeo player origin for secure postMessage API communication
const VIMEO_ORIGIN = 'https://player.vimeo.com'

export default class extends Controller {
  connect() {
    // Only connect if we're on the how-it-works page
    if (!document.body.classList.contains('how-it-works')) {
      return
    }

    // Store bound references to enable proper cleanup in disconnect()
    this.boundHandleOpen = this.handleDialogOpen.bind(this)
    this.boundHandleClose = this.handleDialogClose.bind(this)

    // Listen for dialog:opened and close events using event delegation
    document.addEventListener('dialog:opened', this.boundHandleOpen, true)
    document.addEventListener('close', this.boundHandleClose, true)
  }

  disconnect() {
    // Clean up event listeners using stored bound references
    if (this.boundHandleOpen) {
      document.removeEventListener('dialog:opened', this.boundHandleOpen, true)
      document.removeEventListener('close', this.boundHandleClose, true)
    }

    // Cancel pending playVideo timeout if controller disconnects
    if (this.playVideoTimeout) {
      clearTimeout(this.playVideoTimeout)
      this.playVideoTimeout = null
    }
  }

  handleDialogOpen(event) {
    const dialog = event.target
    // Only handle video-modal dialogs
    if (!dialog.matches('dialog.modal.video-modal')) {
      return
    }

    // Small delay to ensure iframe is ready, then play video with sound
    this.playVideoTimeout = setTimeout(() => {
      this.playVideo(dialog)
      this.playVideoTimeout = null
    }, IFRAME_READY_DELAY)
  }

  // Send a command to all Vimeo iframes in the dialog
  sendVimeoCommand(dialog, command) {
    const iframes = dialog.querySelectorAll('iframe')
    iframes.forEach(iframe => {
      if (iframe.src.includes('player.vimeo.com') && iframe.contentWindow) {
        try {
          iframe.contentWindow.postMessage(`{"method":"${command}"}`, VIMEO_ORIGIN)
        } catch (error) {
          console.warn(`Failed to ${command} Vimeo video:`, error)
        }
      }
    })
  }

  playVideo(dialog) {
    this.sendVimeoCommand(dialog, 'play')
  }

  handleDialogClose(event) {
    const dialog = event.target
    // Only handle video-modal dialogs
    if (!dialog.matches('dialog.modal.video-modal')) {
      return
    }

    this.sendVimeoCommand(dialog, 'pause')
  }
}

